/**
 * Vehicle damage-diagram SVG library (Iter 67ec).
 *
 * Hand-drawn, freely-licensable line-art outlines covering every body
 * family we support in the car-creation list. Each family supplies four
 * views (front / rear / left / right) — the inspection canvas overlays
 * damage markers on top of these.
 *
 * Why hand-drawn inline SVG instead of downloaded clip-art:
 *   • Zero external assets / zero CDN dependency / installable as PWA
 *   • Predictable viewBox (0 0 100 60 → easy normalized x/y coords)
 *   • Matches the platform's amber/black aesthetic via `currentColor`
 *   • Hot-pluggable per body type via the `BODY_FAMILY` map
 *
 * `bodyFamilyFor(name)` collapses the marketing label vendors pick on
 * the car form (e.g. "Wagon / Estate", "Cabriolet / Convertible",
 * "Electric & Hybrid") down to the small set of distinct silhouettes
 * we actually maintain. Future fidelity upgrades replace one entry in
 * BODY_FAMILY without touching call-sites.
 */

// ── Body-family resolver ──────────────────────────────────────────────
// Maps every body label the platform accepts → the canonical family
// silhouette we render. Lower-case for forgiving matching; partial
// substring matches catch composite labels like "Van / Minivan".
const FAMILY_RULES = [
  { test: /pickup|truck/i,                  family: "pickup" },
  { test: /minivan|mpv|van/i,               family: "van" },
  { test: /wagon|estate/i,                  family: "wagon" },
  { test: /4x4|off.?road|crossover|suv/i,   family: "suv" },
  { test: /coup[eé]/i,                      family: "coupe" },
  { test: /convertible|cabrio/i,            family: "convertible" },
  { test: /hatchback|economy|compact/i,     family: "hatchback" },
  { test: /sedan|saloon|luxury/i,           family: "sedan" },
  { test: /sport/i,                         family: "coupe" },
  { test: /electric|hybrid/i,               family: "sedan" }, // body shape varies — sedan is the safest default
];

export function bodyFamilyFor(bodyTypeLabel) {
  const label = String(bodyTypeLabel || "").trim();
  if (!label) return "sedan";
  for (const r of FAMILY_RULES) if (r.test.test(label)) return r.family;
  return "sedan";
}

// ── SVG outline builders ──────────────────────────────────────────────
// All bodies use viewBox "0 0 100 60". The stroke uses `currentColor` so
// the canvas can theme them (default neutral-800; hover/active brighter).
// Path shapes are intentionally simple — they read as "car silhouette"
// at any zoom level + render cheaply inside the PDF later.

// Top-level wrapper that adds the consistent grid + label background so
// every view feels like it came from the same engineering blueprint.
function _frame(children, opts = {}) {
  const { label, accentColor = "#0a0a0a" } = opts;
  return (
    <g>
      {/* Faint grid for placement reference (decorative only). */}
      <defs>
        <pattern id="diag-grid" width="5" height="5" patternUnits="userSpaceOnUse">
          <path d="M 5 0 L 0 0 0 5" fill="none" stroke="rgba(0,0,0,0.04)" strokeWidth="0.25" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="100" height="60" fill="url(#diag-grid)" />
      <g fill="none" stroke={accentColor} strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round">
        {children}
      </g>
      {label && (
        <text x="50" y="58" textAnchor="middle" fontSize="3" fontWeight="600" fill={accentColor} opacity="0.55"
              style={{ letterSpacing: "0.15em", textTransform: "uppercase" }}>
          {label}
        </text>
      )}
    </g>
  );
}

// ── SEDAN ─────────────────────────────────────────────────────────────
function SedanSide() {
  return _frame([
    /* Body silhouette */
    <path key="b" d="M 8 38 Q 10 24 22 22 L 38 18 Q 50 14 62 18 L 78 22 Q 90 24 92 38 L 92 44 Q 80 46 50 46 Q 20 46 8 44 Z" />,
    /* Roof / window line */
    <path key="r" d="M 22 22 L 36 16 L 64 16 L 78 22" />,
    /* Door split */
    <line key="d" x1="50" y1="22" x2="50" y2="44" />,
    /* Wheels */
    <circle key="w1" cx="26" cy="46" r="6" />,
    <circle key="w2" cx="74" cy="46" r="6" />,
    /* Wheel-arch curves */
    <path key="a1" d="M 18 44 Q 26 36 34 44" />,
    <path key="a2" d="M 66 44 Q 74 36 82 44" />,
    /* Headlight */
    <path key="h" d="M 9 36 L 14 34" />,
    /* Door handles */
    <line key="dh1" x1="40" y1="32" x2="46" y2="32" />,
    <line key="dh2" x1="60" y1="32" x2="66" y2="32" />,
    /* Iter 67ff — Wing mirror on the A-pillar / front door corner */
    <path key="mirror" d="M 22 23 L 19 22.5 L 18.5 25 L 21 25.5 Z" />,
  ], { label: "Side view" });
}
function SedanFront() {
  return _frame([
    <path key="b" d="M 14 42 Q 14 18 28 16 L 72 16 Q 86 18 86 42 L 86 48 L 14 48 Z" />,
    <path key="w" d="M 24 18 L 28 26 L 72 26 L 76 18" />,                 /* windshield */
    <line key="bonnet" x1="14" y1="32" x2="86" y2="32" />,
    <rect key="grille" x="38" y="34" width="24" height="6" rx="1" />,
    <circle key="hL" cx="22" cy="36" r="3" />,
    <circle key="hR" cx="78" cy="36" r="3" />,
    <line key="bumper" x1="14" y1="46" x2="86" y2="46" />,
    <rect key="plate" x="44" y="42" width="12" height="4" />,
    /* Iter 67ff — Wing mirrors poke out either side of the windshield */
    <path key="mL" d="M 14 24 L 10 25 L 10 28 L 14 29 Z" />,
    <path key="mR" d="M 86 24 L 90 25 L 90 28 L 86 29 Z" />,
  ], { label: "Front view" });
}
function SedanRear() {
  return _frame([
    <path key="b" d="M 14 42 Q 14 18 28 16 L 72 16 Q 86 18 86 42 L 86 48 L 14 48 Z" />,
    <path key="w" d="M 24 18 L 28 26 L 72 26 L 76 18" />,
    <line key="trunk" x1="14" y1="30" x2="86" y2="30" />,
    <rect key="tL" x="18" y="32" width="14" height="6" rx="1" />,
    <rect key="tR" x="68" y="32" width="14" height="6" rx="1" />,
    <line key="bumper" x1="14" y1="46" x2="86" y2="46" />,
    <rect key="plate" x="40" y="40" width="20" height="5" />,
    <text key="lbl" x="50" y="44" textAnchor="middle" fontSize="2.4" stroke="none" fill="currentColor" opacity="0.6">REG</text>,
  ], { label: "Rear view" });
}

// ── HATCHBACK ─────────────────────────────────────────────────────────
function HatchSide() {
  return _frame([
    <path key="b" d="M 10 38 Q 12 24 22 22 L 38 18 Q 50 16 60 18 L 78 26 Q 88 28 90 38 L 90 44 Q 80 46 50 46 Q 20 46 10 44 Z" />,
    <path key="r" d="M 22 22 L 38 16 L 60 16 L 78 26" />,
    <line key="d" x1="48" y1="22" x2="48" y2="44" />,
    <circle key="w1" cx="26" cy="46" r="6" />,
    <circle key="w2" cx="74" cy="46" r="6" />,
    <path key="a1" d="M 18 44 Q 26 36 34 44" />,
    <path key="a2" d="M 66 44 Q 74 36 82 44" />,
    <path key="h" d="M 9 36 L 14 34" />,
    <line key="dh1" x1="36" y1="32" x2="42" y2="32" />,
    <line key="dh2" x1="56" y1="32" x2="62" y2="32" />,
    /* Iter 67ff — Wing mirror */
    <path key="mirror" d="M 22 23 L 19 22.5 L 18.5 25 L 21 25.5 Z" />,
  ], { label: "Side view" });
}

// ── SUV ───────────────────────────────────────────────────────────────
function SuvSide() {
  return _frame([
    <path key="b" d="M 8 36 Q 10 18 22 16 L 78 16 Q 90 18 92 36 L 92 44 Q 80 46 50 46 Q 20 46 8 44 Z" />,
    <line key="r" x1="22" y1="16" x2="78" y2="16" />,
    <line key="w" x1="22" y1="22" x2="78" y2="22" />,
    <line key="d" x1="50" y1="22" x2="50" y2="44" />,
    <circle key="w1" cx="26" cy="46" r="7" />,
    <circle key="w2" cx="74" cy="46" r="7" />,
    <path key="a1" d="M 17 44 Q 26 34 35 44" />,
    <path key="a2" d="M 65 44 Q 74 34 83 44" />,
    /* Iter 67ff — Wing mirror on the A-pillar */
    <path key="mirror" d="M 22 18 L 18.5 17.5 L 18 20 L 21 20.5 Z" />,
  ], { label: "Side view" });
}
function SuvFront() {
  return _frame([
    <path key="b" d="M 12 42 Q 12 14 26 12 L 74 12 Q 88 14 88 42 L 88 50 L 12 50 Z" />,
    <line key="bonnet" x1="12" y1="30" x2="88" y2="30" />,
    <rect key="grille" x="34" y="32" width="32" height="8" rx="1" />,
    <circle key="hL" cx="22" cy="34" r="3.5" />,
    <circle key="hR" cx="78" cy="34" r="3.5" />,
    <line key="bumper" x1="12" y1="46" x2="88" y2="46" />,
    <rect key="plate" x="42" y="42" width="16" height="4" />,
    /* Iter 67ff — Wing mirrors poke out either side */
    <path key="mL" d="M 12 22 L 8 23 L 8 26 L 12 27 Z" />,
    <path key="mR" d="M 88 22 L 92 23 L 92 26 L 88 27 Z" />,
  ], { label: "Front view" });
}

// ── VAN / MINIVAN ─────────────────────────────────────────────────────
function VanSide() {
  return _frame([
    <path key="b" d="M 8 14 L 78 14 Q 90 16 92 32 L 92 44 Q 80 46 50 46 Q 20 46 8 44 Z" />,
    <line key="w" x1="14" y1="18" x2="78" y2="18" />,
    <line key="d1" x1="34" y1="20" x2="34" y2="44" />,
    <line key="d2" x1="58" y1="20" x2="58" y2="44" />,
    <circle key="w1" cx="26" cy="46" r="6" />,
    <circle key="w2" cx="74" cy="46" r="6" />,
    <path key="a1" d="M 18 44 Q 26 36 34 44" />,
    <path key="a2" d="M 66 44 Q 74 36 82 44" />,
    /* Iter 67ff — Wing mirror near the cab front */
    <path key="mirror" d="M 14 16 L 11 15 L 10.5 18 L 13.5 19 Z" />,
  ], { label: "Side view" });
}
function VanFront() {
  return _frame([
    <path key="b" d="M 12 44 L 12 12 L 88 12 L 88 44 L 88 50 L 12 50 Z" />,
    <line key="g" x1="12" y1="28" x2="88" y2="28" />,
    <rect key="ws" x="20" y="14" width="60" height="12" />,
    <circle key="hL" cx="20" cy="40" r="3" />,
    <circle key="hR" cx="80" cy="40" r="3" />,
    <rect key="plate" x="42" y="42" width="16" height="4" />,
    /* Iter 67ff — wide vans get bigger wing mirrors */
    <path key="mL" d="M 12 18 L 7 19 L 7 24 L 12 25 Z" />,
    <path key="mR" d="M 88 18 L 93 19 L 93 24 L 88 25 Z" />,
  ], { label: "Front view" });
}

// ── WAGON / ESTATE ────────────────────────────────────────────────────
function WagonSide() {
  return _frame([
    <path key="b" d="M 8 38 Q 10 22 22 22 L 86 22 Q 92 26 92 38 L 92 44 Q 80 46 50 46 Q 20 46 8 44 Z" />,
    <path key="r" d="M 22 22 L 32 16 L 80 16 L 86 22" />,
    <line key="d" x1="50" y1="22" x2="50" y2="44" />,
    <circle key="w1" cx="26" cy="46" r="6" />,
    <circle key="w2" cx="74" cy="46" r="6" />,
    <path key="a1" d="M 18 44 Q 26 36 34 44" />,
    <path key="a2" d="M 66 44 Q 74 36 82 44" />,
    /* Iter 67ff — Wing mirror */
    <path key="mirror" d="M 22 23 L 19 22.5 L 18.5 25 L 21 25.5 Z" />,
  ], { label: "Side view" });
}

// ── COUPE / SPORT ─────────────────────────────────────────────────────
function CoupeSide() {
  return _frame([
    <path key="b" d="M 8 40 Q 12 26 22 22 L 50 16 Q 70 14 78 22 L 88 26 Q 92 30 92 40 L 92 44 Q 80 46 50 46 Q 20 46 8 44 Z" />,
    <path key="r" d="M 22 22 L 50 14 L 78 22" />,
    <line key="d" x1="50" y1="22" x2="50" y2="44" />,
    <circle key="w1" cx="26" cy="46" r="6" />,
    <circle key="w2" cx="74" cy="46" r="6" />,
    <path key="a1" d="M 18 44 Q 26 36 34 44" />,
    <path key="a2" d="M 66 44 Q 74 36 82 44" />,
    /* Iter 67ff — Sport mirror sits low on the door */
    <path key="mirror" d="M 22 23 L 19 23 L 18.5 25.5 L 21 25.5 Z" />,
  ], { label: "Side view" });
}

// ── CONVERTIBLE ───────────────────────────────────────────────────────
function ConvertibleSide() {
  return _frame([
    <path key="b" d="M 8 38 Q 12 26 22 24 L 80 24 Q 92 26 92 38 L 92 44 Q 80 46 50 46 Q 20 46 8 44 Z" />,
    <path key="d" d="M 22 24 Q 36 20 50 22 Q 64 24 76 22" strokeDasharray="2,1.4" />,
    <circle key="w1" cx="26" cy="46" r="6" />,
    <circle key="w2" cx="74" cy="46" r="6" />,
    <path key="a1" d="M 18 44 Q 26 36 34 44" />,
    <path key="a2" d="M 66 44 Q 74 36 82 44" />,
    /* Iter 67ff — Wing mirror */
    <path key="mirror" d="M 22 25 L 19 24.5 L 18.5 27 L 21 27.5 Z" />,
  ], { label: "Side view (top down)" });
}

// ── PICKUP ────────────────────────────────────────────────────────────
function PickupSide() {
  return _frame([
    <path key="cab" d="M 8 38 Q 10 22 22 22 L 50 22 L 56 26 L 56 38 Z" />,
    <path key="bed" d="M 56 26 L 92 26 L 92 38 L 56 38 Z" />,
    <path key="r" d="M 22 22 L 32 16 L 50 16 L 50 22" />,
    <circle key="w1" cx="26" cy="46" r="6" />,
    <circle key="w2" cx="74" cy="46" r="6" />,
    <path key="a1" d="M 18 44 Q 26 36 34 44" />,
    <path key="a2" d="M 66 44 Q 74 36 82 44" />,
    <line key="floor" x1="8" y1="44" x2="92" y2="44" />,
    /* Iter 67ff — Wing mirror on the cab front */
    <path key="mirror" d="M 22 23 L 18.5 22.5 L 18 25 L 21 25.5 Z" />,
  ], { label: "Side view" });
}

// ── TOP-DOWN VIEWS (Iter 67ed) ────────────────────────────────────────
// Bird's-eye silhouettes. The renter / vendor can plot damage on the
// roof, bonnet, or boot here when a side/front view doesn't capture it
// (e.g. a hail-storm dent on the roof, a tree-branch scratch across the
// top of the windscreen). Same viewBox (0..100 × 0..60) so marker coords
// stay consistent across views.
function SedanTop() {
  return _frame([
    /* Outer body silhouette */
    <path key="b" d="M 10 18 Q 12 8 22 6 L 78 6 Q 88 8 90 18 L 90 42 Q 88 52 78 54 L 22 54 Q 12 52 10 42 Z" />,
    /* Bonnet break */
    <line key="bonnet" x1="10" y1="18" x2="90" y2="18" />,
    /* Windshield */
    <path key="ws" d="M 18 18 L 24 24 L 76 24 L 82 18" />,
    /* Roof rectangle */
    <rect key="roof" x="24" y="24" width="52" height="14" />,
    /* Rear window */
    <path key="rw" d="M 18 42 L 24 38 L 76 38 L 82 42" />,
    /* Boot break */
    <line key="boot" x1="10" y1="42" x2="90" y2="42" />,
    /* Side mirrors */
    <line key="ml" x1="9" y1="22" x2="13" y2="22" />,
    <line key="mr" x1="87" y1="22" x2="91" y2="22" />,
  ], { label: "Top view" });
}
function SuvTop() {
  return _frame([
    <rect key="b" x="10" y="6" width="80" height="48" rx="6" />,
    <line key="bonnet" x1="10" y1="16" x2="90" y2="16" />,
    <path key="ws" d="M 18 16 L 22 22 L 78 22 L 82 16" />,
    <rect key="roof" x="22" y="22" width="56" height="18" />,
    <path key="rw" d="M 18 44 L 22 40 L 78 40 L 82 44" />,
    <line key="boot" x1="10" y1="44" x2="90" y2="44" />,
    <line key="ml" x1="9" y1="20" x2="13" y2="20" />,
    <line key="mr" x1="87" y1="20" x2="91" y2="20" />,
  ], { label: "Top view" });
}
function VanTop() {
  return _frame([
    <rect key="b" x="10" y="6" width="80" height="48" rx="3" />,
    <rect key="ws" x="12" y="8" width="76" height="6" />,
    <rect key="rw" x="12" y="46" width="76" height="6" />,
    <rect key="roof" x="14" y="14" width="72" height="32" />,
    <line key="ml" x1="9" y1="18" x2="13" y2="18" />,
    <line key="mr" x1="87" y1="18" x2="91" y2="18" />,
  ], { label: "Top view" });
}
function WagonTop() {
  return _frame([
    <path key="b" d="M 10 18 Q 12 8 22 6 L 86 6 Q 90 10 90 18 L 90 42 Q 90 50 86 54 L 22 54 Q 12 52 10 42 Z" />,
    <line key="bonnet" x1="10" y1="18" x2="90" y2="18" />,
    <path key="ws" d="M 18 18 L 24 24 L 84 24 L 88 20" />,
    <rect key="roof" x="24" y="24" width="60" height="16" />,
    <line key="boot" x1="10" y1="40" x2="90" y2="40" />,
    <line key="ml" x1="9" y1="22" x2="13" y2="22" />,
    <line key="mr" x1="87" y1="22" x2="91" y2="22" />,
  ], { label: "Top view" });
}
function CoupeTop() {
  return _frame([
    <path key="b" d="M 14 20 Q 16 10 26 8 L 50 6 Q 70 6 78 10 Q 88 14 88 22 L 88 40 Q 86 50 76 52 L 24 52 Q 14 50 12 40 Z" />,
    <line key="bonnet" x1="14" y1="20" x2="88" y2="20" />,
    <path key="ws" d="M 22 20 L 30 26 L 70 26 L 78 20" />,
    <ellipse key="roof" cx="50" cy="32" rx="24" ry="8" />,
    <path key="rw" d="M 22 40 L 30 36 L 70 36 L 78 40" />,
    <line key="boot" x1="14" y1="42" x2="88" y2="42" />,
  ], { label: "Top view" });
}
function ConvertibleTop() {
  return _frame([
    <path key="b" d="M 10 18 Q 12 8 22 6 L 78 6 Q 88 8 90 18 L 90 42 Q 88 52 78 54 L 22 54 Q 12 52 10 42 Z" />,
    <line key="bonnet" x1="10" y1="18" x2="90" y2="18" />,
    /* Open cabin – stitched outline */
    <path key="cabin" d="M 24 18 L 28 24 L 72 24 L 76 18 L 76 42 L 72 36 L 28 36 L 24 42 Z" strokeDasharray="2,1.4" />,
    <line key="boot" x1="10" y1="42" x2="90" y2="42" />,
    <line key="ml" x1="9" y1="22" x2="13" y2="22" />,
    <line key="mr" x1="87" y1="22" x2="91" y2="22" />,
  ], { label: "Top view (cabin open)" });
}
function PickupTop() {
  return _frame([
    <path key="b" d="M 12 8 L 88 8 L 88 52 L 12 52 Z" />,
    <line key="bonnet" x1="12" y1="16" x2="88" y2="16" />,
    <rect key="cabin" x="14" y="16" width="72" height="18" />,
    <line key="cabin-back" x1="12" y1="34" x2="88" y2="34" />,
    <text key="bed-label" x="50" y="46" textAnchor="middle" fontSize="3" stroke="none" fill="currentColor" opacity="0.45"
          style={{ letterSpacing: "0.2em", textTransform: "uppercase" }}>Cargo bed</text>,
  ], { label: "Top view" });
}

// ── BODY_FAMILY map ───────────────────────────────────────────────────
// Each entry supplies the five views. Where a body family doesn't have
// a meaningfully different front/rear from the sedan (most don't), we
// reuse the sedan render for those views. Side views remain unique.
export const BODY_FAMILY = {
  sedan:        { name: "Sedan",         side: SedanSide,        front: SedanFront, rear: SedanRear, left: SedanSide,        right: SedanSide,        top: SedanTop },
  hatchback:    { name: "Hatchback",     side: HatchSide,        front: SedanFront, rear: SedanRear, left: HatchSide,        right: HatchSide,        top: SedanTop },
  suv:          { name: "SUV",           side: SuvSide,          front: SuvFront,   rear: SuvSide,   left: SuvSide,          right: SuvSide,          top: SuvTop },
  van:          { name: "Van / Minivan", side: VanSide,          front: VanFront,   rear: VanFront,  left: VanSide,          right: VanSide,          top: VanTop },
  wagon:        { name: "Wagon / Estate", side: WagonSide,       front: SedanFront, rear: SedanRear, left: WagonSide,        right: WagonSide,        top: WagonTop },
  coupe:        { name: "Coupe",         side: CoupeSide,        front: SedanFront, rear: SedanRear, left: CoupeSide,        right: CoupeSide,        top: CoupeTop },
  convertible:  { name: "Convertible",   side: ConvertibleSide,  front: SedanFront, rear: SedanRear, left: ConvertibleSide,  right: ConvertibleSide,  top: ConvertibleTop },
  pickup:       { name: "Pickup",        side: PickupSide,       front: SuvFront,   rear: PickupSide, left: PickupSide,       right: PickupSide,       top: PickupTop },
};

/**
 * Returns the renderable component for `family/view` with a graceful
 * fallback chain so callers never have to guard on missing views.
 */
export function diagramFor(family, view) {
  const fam = BODY_FAMILY[family] || BODY_FAMILY.sedan;
  return fam[view] || fam.side || SedanSide;
}

export const ALL_VIEWS = ["front", "left", "right", "rear", "top"];
